import React, { useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Container,
  BackButton,
  Button,
  KeenIcon,
  Shimmer,
} from '@/components';
import { Toolbar, ToolbarHeading, ToolbarPageTitle } from '@/partials/toolbar';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  useGetClubBookingByIdQuery,
  useReturnClubResourceKeysMutation,
  useCancelClubBookingMutation,
  useCompleteInspectionMutation,
} from '@/services/ClubBookingService';
import {
  ClubBookingStatus,
  ResourceType,
  ClubBooking,
  ClubBookingItem,
  PaymentTransaction,
} from '@/types/club-booking-management.types';
import { usePermissions } from '@/hooks/usePermissions';
import { useSnackbar } from 'notistack';

import { formatFinnishCurrency } from '@/utils/Currency';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';

const ClubBookingDetails = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    action: 'return_keys' | 'cancel' | 'complete_inspection' | null;
    reason?: string;
    itemId?: string;
    quantity?: number;
  }>({ isOpen: false, action: null });

  const [completingInspection, setCompletingInspection] = useState<
    string | null
  >(null);

  const {
    data: bookingData,
    isLoading,
    isError,
    refetch,
  } = useGetClubBookingByIdQuery(id || '', { skip: !id });

  const booking = bookingData as unknown as ClubBooking;

  const [returnClubResourceKeys] = useReturnClubResourceKeysMutation();
  const [cancelClubBooking] = useCancelClubBookingMutation();
  const [completeInspection] = useCompleteInspectionMutation();

  const groupedItems = useMemo(() => {
    if (!booking?.items || booking.items.length === 0) {
      return [] as ClubBookingItem[];
    }

    const groups = new Map<string, ClubBookingItem[]>();

    booking.items.forEach((item) => {
      if (
        item.resource.resource_type === ResourceType.KEYS ||
        item.resource.resource_type === ResourceType.SAUNA_BOOKING
      ) {
        const groupKey = `${item.resource.id}-${item.resource.resource_type}`;
        const existing = groups.get(groupKey);
        if (existing) {
          existing.push(item);
        } else {
          groups.set(groupKey, [item]);
        }
      } else {
        groups.set(`single-${item.id}`, [item]);
      }
    });

    const aggregated: ClubBookingItem[] = [];

    groups.forEach((items, key) => {
      if (
        items.length > 1 &&
        (items[0].resource.resource_type === ResourceType.KEYS ||
          items[0].resource.resource_type === ResourceType.SAUNA_BOOKING)
      ) {
        const earliestStartItem = items.reduce((earliest, current) =>
          new Date(current.start_time) < new Date(earliest.start_time)
            ? current
            : earliest
        );

        const latestEndItem = items.reduce((latest, current) =>
          new Date(current.end_time) > new Date(latest.end_time)
            ? current
            : latest
        );

        const totalPrice = items.reduce(
          (sum, current) => sum + (current.total_price || 0),
          0
        );

        const totalQuantity = items.reduce(
          (sum, current) => sum + (current.quantity || 0),
          0
        );

        const base = items[0];

        aggregated.push({
          ...base,
          id: key,
          start_time: earliestStartItem.start_time,
          end_time: latestEndItem.end_time,
          total_price: totalPrice,
          quantity: totalQuantity,
        });
      } else {
        aggregated.push(items[0]);
      }
    });

    aggregated.sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    return aggregated;
  }, [booking?.items]);

  const extractErrorMessage = (err: unknown): string => {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object') {
      const obj = err as Record<string, unknown>;
      const data = obj['data'] as unknown;
      if (typeof data === 'string') return data;
      if (data && typeof data === 'object') {
        const d = data as Record<string, unknown>;
        if (typeof d['message'] === 'string') return d['message'] as string;
        if (typeof d['error'] === 'string') return d['error'] as string;
        if (d['error'] && typeof d['error'] === 'object') {
          const inner = d['error'] as Record<string, unknown>;
          if (typeof inner['message'] === 'string')
            return inner['message'] as string;
        }
      }
      if (typeof obj['error'] === 'string') return obj['error'] as string;
      if (typeof obj['message'] === 'string') return obj['message'] as string;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return 'Failed to update booking';
    }
  };

  const getResourceTypeLabel = (type: ResourceType): string => {
    switch (type) {
      case ResourceType.SAUNA_BOOKING:
        return 'Sauna Booking';
      case ResourceType.KEYS:
        return 'Keys';
      case ResourceType.ANNUAL_INSPECTIONS:
        return 'Annual Inspections';
      case ResourceType.PORT_WATCH_SHIFTS:
        return 'Port Watch Shifts';
      default:
        return 'Unknown';
    }
  };

  const getStatusLabel = (status: ClubBookingStatus): string => {
    switch (status) {
      case ClubBookingStatus.PENDING:
        return 'Payment Pending';
      case ClubBookingStatus.CONFIRMED:
        return 'Confirmed';
      case ClubBookingStatus.CANCELLED:
        return 'Cancelled';
      case ClubBookingStatus.COMPLETED:
        return 'Completed';
      case ClubBookingStatus.PENDING_CONFIRMATION:
        return 'Pending Confirmation';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: ClubBookingStatus): string => {
    switch (status) {
      case ClubBookingStatus.CONFIRMED:
        return 'text-green-600 contrast:text-green-200 bg-green-50 dark:bg-green-950 contrast:bg-green-950';
      case ClubBookingStatus.CANCELLED:
        return 'text-red-600 contrast:text-red-200 bg-red-50 dark:bg-red-950 contrast:bg-red-950';
      case ClubBookingStatus.COMPLETED:
        return 'text-blue-600 contrast:text-blue-200 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950';
      case ClubBookingStatus.PENDING_CONFIRMATION:
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-yellow-600 bg-yellow-50';
    }
  };

  const getPaymentStatusColor = (status: string): string => {
    switch (status) {
      case 'SUCCEEDED':
        return 'text-green-600 contrast:text-green-200 bg-green-50 dark:bg-green-950 contrast:bg-green-950';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-red-600 bg-red-50';
    }
  };

  const formatEuroAmount = (amount?: number | null): string => {
    if (amount === null || amount === undefined || amount === 0) {
      return '-';
    }
    return formatFinnishCurrency(amount, {
      currency: 'EUR',
      showCurrency: true,
    });
  };

  const handleReturnKeys = () => {
    setConfirmationDialog({
      isOpen: true,
      action: 'return_keys',
    });
  };

  const handleCancelBooking = () => {
    setConfirmationDialog({
      isOpen: true,
      action: 'cancel',
    });
  };

  const handleConfirmAction = async () => {
    if (!booking || !confirmationDialog.action) return;

    try {
      if (confirmationDialog.action === 'return_keys') {
        // Get all key items that are confirmed
        const keyItems = booking.items.filter(
          (item) =>
            item.resource.resource_type === ResourceType.KEYS &&
            item.status === ClubBookingStatus.CONFIRMED
        );

        if (keyItems.length === 0) {
          console.error('No confirmed key items found');
          return;
        }

        // Call the return keys API with all key items
        await returnClubResourceKeys({
          items: keyItems.map((item) => ({
            item_id: item.id,
            quantity: item.quantity || 1,
          })),
        }).unwrap();
      } else if (confirmationDialog.action === 'cancel') {
        await cancelClubBooking({
          id: id!,
          data: {
            cancellation_reason:
              confirmationDialog.reason || 'Cancelled by admin',
          },
        }).unwrap();
      } else if (confirmationDialog.action === 'complete_inspection') {
        if (confirmationDialog.itemId && confirmationDialog.quantity) {
          await handleCompleteInspection(
            confirmationDialog.itemId,
            confirmationDialog.quantity
          );
          return; // Early return as handleCompleteInspection handles dialog closure
        }
      }

      setConfirmationDialog({ isOpen: false, action: null });
      refetch();
    } catch (error) {
      console.error('Error updating booking:', error);
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setConfirmationDialog((prev) => ({ ...prev, reason: e.target.value }));
  };

  const hasConfirmedKeys = booking?.items.some(
    (item) =>
      item.resource.resource_type === ResourceType.KEYS &&
      item.status === ClubBookingStatus.CONFIRMED
  );

  // Handle complete inspection confirmation
  const handleCompleteInspectionClick = (itemId: string, quantity: number) => {
    setConfirmationDialog({
      isOpen: true,
      action: 'complete_inspection',
      itemId: itemId,
      quantity: quantity,
    });
  };

  // Handle complete inspection
  const handleCompleteInspection = async (itemId: string, quantity: number) => {
    try {
      setCompletingInspection(itemId);
      await completeInspection({
        itemId: itemId,
        quantity: quantity,
      }).unwrap();

      // Refetch booking data to get updated status
      refetch();
      setConfirmationDialog({ isOpen: false, action: null });
    } catch (error) {
      console.error('Error completing inspection:', error);
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    } finally {
      setCompletingInspection(null);
    }
  };

  const backPath = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const clubId = searchParams.get('clubId');
    const fromQuery = searchParams.get('from');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fromState = (location.state as any)?.from as string | undefined;

    if ((fromQuery === 'club' || fromState === 'club-details') && clubId) {
      return `/club-management/${clubId}?tab=resource-bookings`;
    }

    return '/club-booking-management';
  }, [location.search, location.state]);

  return (
    <Container>
      <Toolbar>
        <ToolbarHeading>
          <div className="flex gap-2 items-center">
            <BackButton customBackPath={backPath} />
            <ToolbarPageTitle text="Booking Details" />
          </div>
        </ToolbarHeading>
        {!isLoading && !isError && booking && (
          <div className="flex flex-wrap gap-2.5 justify-center items-center mt-4 sm:mt-0">
            {hasConfirmedKeys && (
              <Button
                type="button"
                variant="primary"
                onClick={handleReturnKeys}
                disabled={isLoading}
              >
                <span className="flex items-center gap-2">
                  <KeenIcon icon="key" className="text-white" />
                  Return Keys
                </span>
              </Button>
            )}
            {booking.status !== ClubBookingStatus.PENDING_CONFIRMATION &&
              booking.status !== ClubBookingStatus.CANCELLED &&
              booking.status !== ClubBookingStatus.COMPLETED &&
              hasPermission('club_resource_booking_management_cancel') && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleCancelBooking}
                  disabled={isLoading}
                >
                  <span className="flex items-center gap-2">
                    <KeenIcon icon="cross" className="text-white" />
                    Cancel Booking
                  </span>
                </Button>
              )}
          </div>
        )}
      </Toolbar>

      {isLoading ? (
        <div className="card mx-auto mb-[30px]">
          <Shimmer height="174px" borderRadius="8px" />
        </div>
      ) : isError || !booking ? (
        <div className="card mx-auto mb-[30px]">
          <div className="p-5 text-red-600" role="alert">
            Error loading booking details. Please try again.
          </div>
        </div>
      ) : (
        <>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Booking Information</h2>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Booking ID
                      </span>
                      <p className="text-gray-900 font-mono">{booking.id}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Status
                      </span>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}
                      >
                        {getStatusLabel(booking.status)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        User
                      </span>
                      <p className="text-gray-900">
                        {booking.user.name || booking.user.email}
                      </p>
                      {booking.user.name && (
                        <p className="text-sm text-gray-500">
                          {booking.user.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Club
                      </span>
                      <p className="text-gray-900">{booking.club.name}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Price
                      </span>
                      <p className="text-gray-900 text-lg font-semibold">
                        {formatEuroAmount(booking.total_price)}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Created At
                      </span>
                      <p className="text-gray-900">
                        {formatDisplayDateTime(new Date(booking.created_at))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Payment Information</h3>
                </div>
                <div className="card-body">
                  {booking.payment_transactions.length > 0 ? (
                    booking.payment_transactions.map(
                      (transaction: PaymentTransaction) => (
                        <div key={transaction.id} className="mb-4 last:mb-0">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-500">
                              Status
                            </span>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getPaymentStatusColor(transaction.status)}`}
                            >
                              {transaction.status}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-500">
                              Amount
                            </span>
                            <span className="text-gray-900">
                              {formatEuroAmount(transaction.amount)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-500">
                              Currency
                            </span>
                            <span className="text-gray-900">
                              {transaction.currency}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-500">
                              Date
                            </span>
                            <span className="text-gray-900 text-sm">
                              {formatDisplayDate(
                                new Date(transaction.created_at)
                              )}
                            </span>
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <p className="text-gray-500">
                      No payment information available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                Booking Items ({groupedItems.length})
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-6">
                {groupedItems.map((item: ClubBookingItem) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {item.resource.name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {getResourceTypeLabel(item.resource.resource_type)}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(item.status)}`}
                      >
                        {getStatusLabel(item.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Start Time
                        </span>
                        <p className="text-gray-900">
                          {formatDisplayDate(item.start_time)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          End Time
                        </span>
                        <p className="text-gray-900">
                          {formatDisplayDate(item.end_time)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Quantity
                        </span>
                        <p className="text-gray-900">{item.quantity || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Price
                        </span>
                        <p className="text-gray-900 font-semibold">
                          {formatFinnishCurrency(item.total_price)}
                        </p>
                      </div>
                    </div>

                    {item.special_requests && (
                      <div className="mt-4">
                        <span className="text-sm font-medium text-gray-500">
                          Special Requests
                        </span>
                        <p className="text-gray-900 mt-1">
                          {item.special_requests}
                        </p>
                      </div>
                    )}

                    {item.cancellation_reason && (
                      <div className="mt-4">
                        <span className="text-sm font-medium text-gray-500">
                          Cancellation Reason
                        </span>
                        <p className="text-red-600 mt-1">
                          {item.cancellation_reason}
                        </p>
                      </div>
                    )}

                    {item.resource.resource_type ===
                      ResourceType.ANNUAL_INSPECTIONS && (
                      <div className="mt-4">
                        {item.inspector ? (
                          <div className="mb-4 p-4 bg-green-50 dark:bg-green-950 contrast:bg-green-950 border border-green-200 dark:border-green-900 contrast:border-green-900 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <KeenIcon
                                icon="user-tick"
                                className="text-green-600 dark:text-green-400 contrast:text-green-400 text-xl"
                              />
                              <span className="text-base font-semibold text-green-800 dark:text-green-200 contrast:text-green-200">
                                Inspector Assigned
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <span className="text-xs font-medium text-green-600 dark:text-green-400 contrast:text-green-400 uppercase tracking-wide">
                                  Inspector Name
                                </span>
                                <div className="text-sm font-medium text-green-800 dark:text-green-200 contrast:text-green-200 mt-1">
                                  {item.inspector.name || 'No name provided'}
                                </div>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-green-600 dark:text-green-400 contrast:text-green-400 uppercase tracking-wide">
                                  Email
                                </span>
                                <div className="text-sm text-green-700 dark:text-green-200 contrast:text-green-200 mt-1">
                                  {item.inspector.email}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-950 contrast:bg-yellow-950 border border-yellow-200 dark:border-yellow-900 contrast:border-yellow-900 rounded-lg">
                            <div className="flex items-center gap-2">
                              <KeenIcon
                                icon="user"
                                className="text-yellow-600 dark:text-yellow-400 contrast:text-yellow-400 text-lg"
                              />
                              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200">
                                No Inspector Assigned
                              </span>
                            </div>
                            <p className="text-xs text-yellow-700 dark:text-yellow-200 contrast:text-yellow-200 mt-1">
                              This annual inspection item does not have an
                              assigned inspector yet.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {item.resource.resource_type ===
                      ResourceType.ANNUAL_INSPECTIONS &&
                      item.status === ClubBookingStatus.CONFIRMED &&
                      hasPermission(
                        'club_resource_booking_management_mark_as_completed'
                      ) && (
                      <div className="mt-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              handleCompleteInspectionClick(
                                item.id,
                                item.quantity || 1
                              )
                            }
                            className="btn btn-sm btn-success flex items-center gap-2"
                            disabled={
                              isLoading || completingInspection === item.id
                            }
                          >
                            {completingInspection === item.id ? (
                              <>
                                <KeenIcon
                                  icon="spinner"
                                  className="text-white animate-spin"
                                />
                                Completing...
                              </>
                            ) : (
                              <>
                                <KeenIcon
                                  icon="check"
                                  className="text-white"
                                />
                                Complete Inspection
                              </>
                            )}
                          </button>
                          {item.inspector ? (
                            <div className="text-sm text-gray-600">
                              Inspector:{' '}
                              <span className="font-medium">
                                {item.inspector.name || item.inspector.email}
                              </span>
                            </div>
                          ) : (
                            <div className="text-sm text-yellow-600 flex items-center gap-1">
                              <KeenIcon
                                icon="warning"
                                className="text-yellow-500"
                              />
                              No inspector assigned
                            </div>
                          )}
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          This will mark the inspection as completed for{' '}
                          {item.quantity || 1} item(s)
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-500">
                            Resource Price
                          </span>
                          <p className="text-gray-900">
                            {formatEuroAmount(item.resource.price)}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">
                            Price Unit
                          </span>
                          <p className="text-gray-900">
                            {item.resource.price_unit || '-'}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">
                            Slot Length
                          </span>
                          <p className="text-gray-900">
                            {item.resource.slot_length || '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmationDialog
        open={confirmationDialog.isOpen}
        onClose={() =>
          setConfirmationDialog({
            isOpen: false,
            action: null,
            reason: undefined,
            itemId: undefined,
            quantity: undefined,
          })
        }
        onConfirm={handleConfirmAction}
        message={
          confirmationDialog.action === 'return_keys' ? (
            <p>Are you sure you want to mark all confirmed keys as returned?</p>
          ) : confirmationDialog.action === 'complete_inspection' ? (
            <div>
              <p>Are you sure you want to mark this inspection as completed?</p>
              <div className="mt-3 p-3 bg-blue-50 rounded-md">
                <div className="text-sm text-blue-800">
                  <strong>Quantity:</strong> {confirmationDialog.quantity || 1}{' '}
                  item(s)
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  This action will change the status to "Completed" and cannot
                  be undone.
                </div>
              </div>
            </div>
          ) : (
            <>
              <p>Are you sure you want to cancel this booking?</p>
              <div className="mb-3">
                <label htmlFor="cancellationReason" className="form-label">
                  Cancellation Reason
                </label>
                <textarea
                  id="cancellationReason"
                  className="form-control bg-light-active w-full textarea mt-3"
                  rows={3}
                  value={confirmationDialog.reason || ''}
                  onChange={handleReasonChange}
                  placeholder="Enter reason for cancellation..."
                />
              </div>
            </>
          )
        }
        cancelButtonText="Cancel"
        confirmButtonText={
          confirmationDialog.action === 'complete_inspection'
            ? 'Complete Inspection'
            : 'Yes'
        }
        aria-labelledby="action-confirmation-title"
        aria-describedby="action-confirmation-description"
      />
    </Container>
  );
};

export default ClubBookingDetails;
